/**
 * "YOU SIGNED IN, BUT THIS APP DID NOT KEEP IT" (live incident, 2026-09-30).
 *
 * Dancers who sign in on the home-screen app were bounced back to an empty
 * sign-in card with no word of why, and families read that as a wrong
 * password and kept resetting it. Reproduced by running the real provider,
 * card and DancerHome against a fake server: the login POST answers 200, the
 * provider says signed-in, DancerHome asks the portal for her data, the
 * portal answers 401 (the cookie never came back), the provider re-checks,
 * and the status call finds no cookie and answers { authenticated: false }
 * with no reason. The password was right. Nothing on screen said so.
 *
 * WHY THE COOKIE WAS NOT KEPT IS SUSPECTED, NOT PROVEN. The leading suspect is
 * the iOS home-screen web app cookie problem (WebKit bug 272325, titled
 * "Session cookies being reset randomly in a Home Screen web app"). Bobbie
 * said "home app", not "iPhone", so the device is an inference to confirm
 * with her. If a stale cookie were the cause, the server would see it and
 * answer 'session_ended', which is the OTHER notice's job, not this one's.
 *
 * The fix is honesty, not a workaround: this page remembers, in memory only,
 * that a login JUST succeeded in this tab. If the very next status answer is
 * "no cookie" with no reason, the cookie was never kept, and the card says
 * so. Nothing here touches the cookie, its attributes, or any server file.
 *
 * WHAT THE EVIDENCE DOES AND DOES NOT PROVE.
 *   - A status answer that carries a reason ('session_ended') is the server
 *     speaking about a cookie it RECEIVED and refused. That is the other
 *     notice's job, and this never overrides it.
 *   - A live cookie on the next answer proves the cookie was kept, so the
 *     memory is dropped the moment any real answer arrives, and also the
 *     moment the portal answers 200 (that call needs the cookie).
 *   - The memory is short lived on purpose. A dancer who signed in fine,
 *     used the app, and lost her cookie hours later for some other reason
 *     must not be told the app "did not keep" a sign-in it kept for hours.
 *     The failure shows up within seconds of the login, so a couple of
 *     minutes is generous.
 *   - Nothing typed is remembered: not the username and not the password.
 *     A retry in the same app spends the same login budget again (every
 *     login does, successful ones included), so the card does not invite one.
 *
 * Pure on purpose: the provider holds the ref and the clock, this decides,
 * and tests/dancer-cookie-not-kept.test.mjs executes it in node.
 */

/** How long after a successful login a "no cookie" answer still counts as "it was never kept". */
export const COOKIE_NOT_KEPT_WINDOW_MS = 2 * 60 * 1000;

export interface JustSignedIn {
  atMs: number;
}

export function rememberJustSignedIn(nowMs: number): JustSignedIn {
  return { atMs: nowMs };
}

export interface StatusAnswer {
  authenticated: boolean;
  reason: 'session_ended' | null;
}

/**
 * True only for the exact signature of a cookie that was never kept: a login
 * succeeded moments ago in this tab, and the status answer says there is no
 * dancer session and gives no reason.
 */
export function dancerCookieWasNotKept(
  status: StatusAnswer,
  justSignedIn: JustSignedIn | null,
  nowMs: number,
): boolean {
  if (status.authenticated) return false;
  if (status.reason !== null) return false;
  if (!justSignedIn) return false;
  const elapsed = nowMs - justSignedIn.atMs;
  return elapsed >= 0 && elapsed <= COOKIE_NOT_KEPT_WINDOW_MS;
}

/**
 * The two cards. The Safari sentence is true advice only inside the iPhone or
 * iPad home-screen app, so it is shown only there; everywhere else the sentence
 * stays honest and sends her to a grown-up. Coordinator-approved copy, which
 * states no studio fact.
 */
export const COOKIE_NOT_KEPT_HOME_SCREEN_NOTICE =
  'You signed in, but this app did not keep your sign-in. Open the Hub in Safari and sign in there, or ask a grown-up to help.';
export const COOKIE_NOT_KEPT_BROWSER_NOTICE =
  'You signed in, but this browser did not keep your sign-in. Ask a grown-up to help.';

export function cookieNotKeptNotice(homeScreenApp: boolean): string {
  return homeScreenApp ? COOKIE_NOT_KEPT_HOME_SCREEN_NOTICE : COOKIE_NOT_KEPT_BROWSER_NOTICE;
}

/**
 * Is this page the iPhone or iPad home-screen app? `navigator.standalone` is
 * true only there (Safari's own property), and it is the signal
 * components/InstallPrompt.tsx already reads. Strictly `=== true`, so a
 * browser that has no such property, or any odd value, never gets the
 * Safari advice. `display-mode: standalone` is NOT used: Android installed
 * apps match it too, and "open it in Safari" would be wrong there.
 */
export function isHomeScreenApp(nav: object | null | undefined): boolean {
  return (nav as { standalone?: unknown } | null | undefined)?.standalone === true;
}
